/**
 * platformLabels — the ONE German name for each publishing destination, and the
 * publish-action copy derived from it.
 *
 * A destination is a `platform` STRING on a post channel. Most are literal networks
 * (`facebook`, `instagram`, `linkedin`); two are carriers that ride a network with a
 * different publish path or format — `facebook_group` (posted by hand via the
 * browser extension; see WorkflowEditor/shared/manualPlatforms) and `facebook_story`
 * (the Instagram-Story exit's mirror, published to the Facebook Page as a Story).
 * Every surface that shows a destination reads from here: the reviewer, the post
 * dashboard, the workflow list and the approval email. They used to keep four
 * copies of the same map, which is exactly how a new destination ends up rendering
 * as a raw `facebook_story` in one place and a proper name in another.
 *
 * ZERO imports (client-safe ESM, webpack-4 bundled, and require()-of-ESM on the
 * server — the same boundary nodeRegistry.js crosses). NO ES2020 logical-assignment
 * (`||=`/`&&=`/`??=`) — CI-guarded by sourceLogicalAssignment.test.js.
 *
 * @module AdminSocialPostsPage/platformLabels
 */

const PLATFORM_LABELS = {
    facebook: 'Facebook',
    facebook_group: 'Facebook-Community',
    facebook_story: 'Facebook · Story',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
};

// The publish action reads "Auf <Plattform> posten" everywhere. A Facebook Story is
// the one destination whose label is a format rather than a place, so it reads the
// other way round instead of the clumsy "Auf Facebook · Story posten".
const PLATFORM_PUBLISH_LABELS = { facebook_story: 'Story auf Facebook posten' };

/**
 * The German name of a destination; the raw platform string when it is unknown, so
 * a new destination is visible rather than blank.
 * @param {string} platform
 * @returns {string}
 */
const platformLabel = platform => PLATFORM_LABELS[platform] || platform;

/**
 * The reviewer's publish-button copy for a destination.
 * @param {string} platform
 * @returns {string}
 */
const publishActionLabel = platform =>
    PLATFORM_PUBLISH_LABELS[platform] || `Auf ${platformLabel(platform)} posten`;

export { PLATFORM_LABELS, platformLabel, publishActionLabel };
